import React, { useEffect, useState } from 'react';
import {
  Grid,
  FormControl,
  Autocomplete,
  TextFieldProps,
  Typography,
} from '@mui/material';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { useLocation } from 'react-router-dom';
import { useAppSelector } from '../../../../hooks';
import { TPTextField } from '../../../../components/Inputs';
import { getCurriculumV2State } from '../../../../store/V2/CurriculumSlice';
import { TState } from '..';
import { getUrlConvertedString } from '../../../../helper';

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      borderRadius: '12px',
      backgroundColor: '#FFFFFF',
    },
  },
};

type TSelectOption = {
  label: string;
  value: string;
};

interface FilterSectionProps {
  state: TState;
  updateStateAttributes: (attributes: Partial<TState>) => void;
}

const FilterSection: React.FC<FilterSectionProps> = ({
  state,
  updateStateAttributes,
}) => {
  const location = useLocation();

  const urlParams = new URLSearchParams(location.search);
  const urlCurriculum = urlParams.get('curriculum');
  const urlSubject = urlParams.get('subject');
  const urlGrade = urlParams.get('grade');

  // Store
  const { list: curriculumList, status } = useAppSelector(getCurriculumV2State);

  // State
  const [curriculumOptions, setCurriculumOptions] = useState<
    Array<TSelectOption>
  >([]);
  const [subjectOptions, setSubjectOptions] = useState<Array<TSelectOption>>(
    []
  );
  const [gradeOptions, setGradeOptions] = useState<Array<TSelectOption>>([]);

  const hasError =
    state.submitClicked &&
    (!state.selectedCurriculum ||
      !state.selectedSubject ||
      !state.selectedGrade);

  useEffect(() => {
    if (!curriculumList.length) return;

    const newValues = { ...state };

    const newCurriculumOptions = curriculumList.map(({ curriculum }) => ({
      label: curriculum,
      value: curriculum,
    }));
    setCurriculumOptions(newCurriculumOptions);

    if (urlCurriculum) {
      const selectedCurriculum = newCurriculumOptions.find(
        (option) => getUrlConvertedString(option.value) === urlCurriculum
      );

      if (selectedCurriculum) {
        newValues.selectedCurriculum = selectedCurriculum;

        const matchingCurriculum = curriculumList.find(
          (item) => item.curriculum === selectedCurriculum.value
        );

        if (matchingCurriculum?.subjects?.length) {
          const newSubjectOptions = matchingCurriculum.subjects.map(
            ({ subject }) => ({
              label: subject,
              value: subject,
            })
          );
          setSubjectOptions(newSubjectOptions);

          if (urlSubject) {
            const selectedSubject = newSubjectOptions.find(
              (option) => getUrlConvertedString(option.value) === urlSubject
            );

            if (selectedSubject) {
              newValues.selectedSubject = selectedSubject;

              const matchingSubject = matchingCurriculum.subjects.find(
                (item) => item.subject === selectedSubject.value
              );

              if (matchingSubject?.grade?.length) {
                const newGradeOptions = matchingSubject.grade.map(
                  ({ grade }) => ({
                    label: grade,
                    value: grade,
                  })
                );
                setGradeOptions(newGradeOptions);

                if (urlGrade) {
                  const selectedGrade = newGradeOptions.find(
                    (option) => getUrlConvertedString(option.value) === urlGrade
                  );

                  if (selectedGrade) {
                    newValues.selectedGrade = selectedGrade;
                  }
                }
              }
            }
          }
        }
      }
    }

    updateStateAttributes({ ...newValues });
  }, [curriculumList]);

  const handleCurriculumChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    const newSubjectOptions: TSelectOption[] = [];

    if (curriculumList && curriculumList.length && value) {
      const selectedCurriculumData = curriculumList.find(
        (item) => item.curriculum === value.value
      );

      if (
        selectedCurriculumData &&
        selectedCurriculumData.subjects &&
        selectedCurriculumData.subjects.length
      ) {
        selectedCurriculumData.subjects.forEach((item) => {
          newSubjectOptions.push({
            label: item.subject,
            value: item.subject,
          });
        });
      }
    }

    updateStateAttributes({
      selectedCurriculum: value,
      selectedSubject: null,
      selectedGrade: null,
    });

    setSubjectOptions(newSubjectOptions);
    setGradeOptions([]);
  };

  const handleSubjectChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    const newGradeOptions: TSelectOption[] = [];

    if (state.selectedCurriculum) {
      const selectedCurriculumData = curriculumList.find(
        (curriculumItem) =>
          curriculumItem.curriculum === state.selectedCurriculum?.value
      );

      if (
        selectedCurriculumData &&
        selectedCurriculumData.subjects &&
        selectedCurriculumData.subjects.length
      ) {
        const selectedSubject = selectedCurriculumData.subjects.find(
          (subjectItem) => subjectItem.subject === value?.value
        );

        if (
          selectedSubject &&
          selectedSubject.grade &&
          selectedSubject.grade.length
        ) {
          selectedSubject.grade.forEach((gradeItem) => {
            newGradeOptions.push({
              label: gradeItem.grade,
              value: gradeItem.grade,
            });
          });
        }
      }
    }

    updateStateAttributes({
      selectedSubject: value,
      selectedGrade: null,
    });

    setGradeOptions(newGradeOptions);
  };

  const handleGradeChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: TSelectOption | null
  ) => {
    updateStateAttributes({
      selectedGrade: value,
    });
  };

  return (
    <>
      <Grid container spacing={2} marginBottom={2}>
        <Grid item xs={12} sm={4} md={3} lg={2.5}>
          <FormControl fullWidth>
            <Autocomplete
              value={state.selectedCurriculum}
              options={curriculumOptions}
              onChange={handleCurriculumChange}
              popupIcon={<KeyboardArrowDownIcon fontSize="small" />}
              loading={status === 'loading'}
              isOptionEqualToValue={(option, value) =>
                option.label === value.value
              }
              getOptionKey={(option: TSelectOption) => option.value}
              renderInput={(params) => (
                <TPTextField
                  {...params}
                  {...textFieldProps}
                  placeholder="Select Curriculum"
                  error={state.submitClicked && !state.selectedCurriculum}
                />
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={2.5}>
          <FormControl fullWidth>
            <Autocomplete
              value={state.selectedSubject}
              options={subjectOptions}
              onChange={handleSubjectChange}
              disabled={!state.selectedCurriculum}
              popupIcon={<KeyboardArrowDownIcon fontSize="small" />}
              loading={status === 'loading'}
              isOptionEqualToValue={(option, value) =>
                option.label === value.value
              }
              getOptionKey={(option: TSelectOption) => option.value}
              renderInput={(params) => (
                <TPTextField
                  {...params}
                  {...textFieldProps}
                  placeholder="Select Subject"
                  error={state.submitClicked && !state.selectedSubject}
                />
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={4} md={3} lg={2.5}>
          <FormControl fullWidth>
            <Autocomplete
              value={state.selectedGrade}
              options={gradeOptions}
              onChange={handleGradeChange}
              disabled={!state.selectedSubject}
              popupIcon={<KeyboardArrowDownIcon fontSize="small" />}
              loading={status === 'loading'}
              isOptionEqualToValue={(option, value) =>
                option.label === value.value
              }
              getOptionKey={(option: TSelectOption) => option.value}
              renderInput={(params) => (
                <TPTextField
                  {...params}
                  {...textFieldProps}
                  placeholder="Select Grade"
                  error={state.submitClicked && !state.selectedGrade}
                />
              )}
            />
          </FormControl>
        </Grid>
      </Grid>
      <Typography color={hasError ? '#e74c3c' : '#6A6A6A'}>
        *Select your curriculum, subject, and grade to find tutors tailored to
        your learning needs.
      </Typography>
    </>
  );
};

export default FilterSection;
